<div class="edi-viewer">
  <div class="viewer-loading" *ngIf="loading">
    <i class="fa-solid fa-spinner fa-spin"></i>
    <span>Creating document view…</span>
  </div>

  <div class="alert alert-danger" *ngIf="error">{{ error }}</div>

  <ng-container *ngIf="view && !loading">
    <header class="document-header">
      <div>
        <span class="eyebrow">EDI document viewer</span>
        <h3>{{ view.definition.title }}</h3>
        <p>{{ view.definition.subtitle }}</p>
      </div>
      <div class="document-actions">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          *ngIf="canRegenerateView"
          [disabled]="loading"
          title="Ignore the saved template and generate a new view"
          (click)="regenerateView()"
        >
          <i class="fa-solid fa-rotate mr-1"></i>
          Regenerate view
        </button>
        <div class="source-file">
          <i class="fa-regular fa-file-code"></i>
          <span>{{ view.source.fileName }}</span>
        </div>
      </div>
    </header>

    <div class="summary-strip">
      <div class="summary-item">
        <span>Message</span>
        <strong>{{ view.source.messageType || 'Unknown' }}</strong>
      </div>
      <div class="summary-item">
        <span>Standard</span>
        <strong>{{ view.source.standard || view.source.syntax }}</strong>
      </div>
      <div class="summary-item">
        <span>Version</span>
        <strong>{{ view.source.version || '—' }}</strong>
      </div>
      <div class="summary-item">
        <span>View</span>
        <strong>{{ view.viewOrigin === 'DETERMINISTIC' ? 'Automatic' : 'AI designed' }}</strong>
      </div>
    </div>

    <div class="alert alert-warning py-2" *ngFor="let warning of viewerWarnings">{{ warning }}</div>

    <nav class="viewer-tabs" aria-label="EDI document views">
      <button type="button" [class.active]="activeTab === 'friendly'" (click)="selectTab('friendly')">
        Friendly view
      </button>
      <button type="button" [class.active]="activeTab === 'structure'" (click)="selectTab('structure')">
        Structure
      </button>
      <button
        type="button"
        *ngIf="canViewRawSource"
        [class.active]="activeTab === 'raw'"
        (click)="selectTab('raw')"
      >
        Raw source
      </button>
      <button
        type="button"
        *ngIf="canCreateMapping"
        [class.active]="activeTab === 'mapping'"
        (click)="selectTab('mapping')"
      >
        Mapping <span class="beta-badge">BETA</span>
      </button>
    </nav>

    <div
      class="viewer-content"
      [class.with-source]="selectedNodes.length > 0 && sourceProvenanceExpanded"
    >
      <main>
        <button
          type="button"
          class="source-provenance-trigger"
          *ngIf="selectedNodes.length && !sourceProvenanceExpanded && activeTab !== 'mapping'"
          aria-label="Expand source provenance"
          [attr.aria-expanded]="false"
          (click)="toggleSourceProvenance()"
        >
          <i class="fa-solid fa-chevron-left"></i>
          <span>
            <small>Source provenance</small>
            <strong>{{ selectedNodes[0].label }}</strong>
          </span>
        </button>
        <ng-container *ngIf="activeTab === 'friendly'">
          <article class="printed-document">
            <header class="printed-document-heading">
              <div>
                <span>{{ view.source.standard || view.source.syntax }}</span>
                <h2>{{ view.definition.title }}</h2>
                <p *ngIf="view.definition.subtitle">{{ view.definition.subtitle }}</p>
              </div>
              <div class="printed-document-mark">
                <strong>{{ view.source.messageType || 'EDI' }}</strong>
                <small>{{ view.source.version }}</small>
              </div>
            </header>

            <div class="printed-document-blocks">
              <lib-edi-view-block
                *ngFor="let block of friendlyBlocks"
                [block]="block"
                [nodeIndex]="nodeIndex"
                (sourceSelected)="selectSource($event)"
              ></lib-edi-view-block>
            </div>
          </article>

          <section class="technical-panel">
            <button type="button" class="technical-toggle" (click)="technicalOpen = !technicalOpen">
              <i class="fa-solid" [ngClass]="technicalOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              <span>Additional & technical information</span>
              <span class="technical-count">{{ fallbackNodes.length }} fields</span>
            </button>
            <div class="technical-values" *ngIf="technicalOpen">
              <button
                type="button"
                class="technical-value"
                *ngFor="let node of visibleFallbackNodes"
                (click)="selectSource([node.id])"
              >
                <span>{{ node.label }}</span>
                <code>{{ node.value ?? node.raw }}</code>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary mt-2"
                *ngIf="visibleFallbackNodes.length < fallbackNodes.length"
                (click)="showMoreTechnical()"
              >
                Show more
              </button>
            </div>
          </section>
        </ng-container>

        <section class="structure-view" *ngIf="activeTab === 'structure'">
          <lib-edi-source-tree-node
            *ngFor="let root of view.source.roots"
            [node]="root"
            [depth]="0"
            [expandedNodeIds]="expandedSourceNodeIds"
            [selectedNodeIds]="selectedSourceNodeIds"
            [scrollTargetId]="scrollTargetNodeId"
            (sourceSelected)="selectSource($event)"
          ></lib-edi-source-tree-node>
        </section>

        <section class="raw-view" *ngIf="canViewRawSource && activeTab === 'raw'">
          <pre>{{ rawDisplay }}</pre>
        </section>

        <section class="embedded-mapping" *ngIf="mappingFile" [hidden]="activeTab !== 'mapping'">
          <lib-edi-mapping-modeller [selectedFile]="mappingFile"></lib-edi-mapping-modeller>
        </section>

        <footer class="coverage-footer" *ngIf="activeTab !== 'mapping'">
          <span class="coverage-ok">
            <i class="fa-solid fa-circle-check"></i>
            Source coverage {{ view.coverage.coveragePercent }}%
          </span>
          <span>No source data omitted</span>
          <span class="coverage-detail">
            {{ view.coverage.explicitlyDisplayed }} friendly · {{ view.coverage.displayedInFallback }} fallback
          </span>
        </footer>
      </main>

      <aside
        class="source-drawer"
        *ngIf="selectedNodes.length && sourceProvenanceExpanded && activeTab !== 'mapping'"
      >
        <div class="drawer-header">
          <div>
            <span class="eyebrow">Source provenance</span>
            <h5>{{ selectedNodes[0].label }}</h5>
          </div>
          <div class="drawer-actions">
            <button
              type="button"
              aria-label="Fold in source provenance"
              [attr.aria-expanded]="true"
              (click)="toggleSourceProvenance()"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
        <div class="source-detail" *ngFor="let node of selectedNodes">
          <span class="node-path">{{ node.path }}</span>
          <pre>{{ node.raw || node.value }}</pre>
          <dl>
            <ng-container *ngFor="let location of node.location | keyvalue">
              <dt>{{ location.key }}</dt>
              <dd>{{ location.value }}</dd>
            </ng-container>
          </dl>
        </div>
        <section class="raw-provenance">
          <div class="raw-provenance-heading">
            <div>
              <span class="eyebrow">Full raw document</span>
              <p>Selected source locations are highlighted</p>
            </div>
            <span>{{ view.source.syntax }}</span>
          </div>
          <pre><ng-container *ngFor="let line of rawSourceLines; let last = last"><ng-container *ngFor="let part of line.parts"><mark *ngIf="part.highlighted">{{ part.text }}</mark><ng-container *ngIf="!part.highlighted">{{ part.text }}</ng-container></ng-container><ng-container *ngIf="!last">{{ '\n' }}</ng-container></ng-container></pre>
        </section>
      </aside>
    </div>
  </ng-container>

  <lib-edi-view-regeneration-dialog
    *ngIf="regenerationDialogOpen"
    [initialInstruction]="view?.regenerationInstruction"
    (confirmed)="confirmRegeneration($event)"
    (cancelled)="regenerationDialogOpen = false"
  ></lib-edi-view-regeneration-dialog>
</div>
